// StaleSyncBanner — banner persistente exibido em todas as páginas quando
// alguma hosting account com auto-sync (whm/hostinger) está com
// last_sync_at > 30 dias. Não dispensa: força o operador a clicar em "Sincronizar"
// pra resolver. Sites apagados no host não são detectados sem sync, então o
// painel diverge da realidade silenciosamente — esse banner cobre o gap.

import { useQuery } from "@tanstack/react-query";
import { Link, useLocation } from "react-router-dom";

import { api } from "../lib/api";
import { HostingAccountResponse, Paginated } from "../types";
import { Icon } from "./Icon";

const STALE_DAYS = 30;
const STALE_MS = STALE_DAYS * 24 * 60 * 60 * 1000;
// Páginas onde NÃO mostrar — fluxos de auth/setup obrigatórios.
const SUPPRESSED_ROUTES = ["/security"];

export function StaleSyncBanner() {
  const location = useLocation();

  // Não mostra durante enrollment forçado de 2FA (a EnrollmentGuard já bloqueia).
  if (SUPPRESSED_ROUTES.includes(location.pathname)) return null;

  // QueryKey hierárquica ["hosting-accounts", "stale-check"] permite que o
  // invalidateQueries({queryKey:["hosting-accounts"]}) feito pelo sync.onSuccess
  // (em pages/HostingAccounts.tsx) refetche o banner automaticamente. Assim,
  // o banner só some quando o backend de fato persistiu last_sync_at — o que
  // o service.go faz APENAS quando SyncHostingAccount completa com sucesso.
  const { data } = useQuery({
    queryKey: ["hosting-accounts", "stale-check"],
    queryFn: () =>
      api<Paginated<HostingAccountResponse>>("/hosting-accounts?limit=200"),
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  });

  if (!data) return null;

  const now = Date.now();
  const stale = (data.data ?? []).filter((a) => {
    if (a.provider === "vps" || a.provider === "manual") return false; // sem auto-sync
    if (!a.has_credential) return false; // não dá pra sincronizar mesmo
    if (!a.last_sync_at) return true; // nunca sincronizou
    const last = new Date(a.last_sync_at).getTime();
    return Number.isFinite(last) && now - last > STALE_MS;
  });

  if (stale.length === 0) return null;

  // Já estamos na página de hosting? Encolhe pra não duplicar a info.
  const isOnHostingPage = location.pathname.startsWith("/hosting-accounts");

  return (
    <div
      role="alert"
      style={{
        position: "sticky",
        top: 0,
        zIndex: 30,
        background: "rgba(245,158,11,0.12)",
        borderBottom: "2px solid var(--warn-500)",
        padding: "12px 28px",
        display: "flex",
        alignItems: "center",
        gap: 14,
      }}
    >
      <div
        style={{
          width: 32,
          height: 32,
          borderRadius: 8,
          background: "rgba(245,158,11,0.2)",
          color: "var(--warn-500)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <Icon name="alert" size={18} stroke={2.5} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 14,
            fontWeight: 700,
            color: "var(--text)",
          }}
        >
          {stale.length === 1
            ? `1 conta de hospedagem está sem sincronizar há mais de ${STALE_DAYS} dias`
            : `${stale.length} contas de hospedagem estão sem sincronizar há mais de ${STALE_DAYS} dias`}
        </div>
        <div
          style={{
            fontSize: 12,
            color: "var(--muted)",
            marginTop: 2,
          }}
        >
          Sites adicionados ou removidos no host nesse período não estão
          refletidos aqui. Sincronize agora pra reconciliar:&nbsp;
          <span style={{ color: "var(--text)", fontWeight: 500 }}>
            {stale
              .slice(0, 3)
              .map((a) => a.name)
              .join(", ")}
            {stale.length > 3 ? ` e mais ${stale.length - 3}` : ""}
          </span>
        </div>
      </div>
      {!isOnHostingPage && (
        <Link
          to="/hosting-accounts"
          className="btn-primary"
          style={{
            fontSize: 12,
            whiteSpace: "nowrap",
            flexShrink: 0,
            background: "var(--warn-500)",
            color: "#000",
          }}
        >
          Ir para Hosting
        </Link>
      )}
    </div>
  );
}
